import type { ProcessCellForExportParams, SuppressKeyboardEventParams } from 'ag-grid-community';

export interface ClipboardChoiceOption {
  readonly id: number;
  readonly label: string;
}

/**
 * Per-column parsing strategy for Excel pastes. `passthrough` keeps AG Grid's
 * raw clipboard value untouched (read-only or free-form columns).
 */
export type ClipboardColumnType =
  | { kind: 'passthrough' }
  | { kind: 'numeric' }
  | { kind: 'text' }
  | { kind: 'date' }
  | { kind: 'choice'; options: readonly ClipboardChoiceOption[] };

/** Comma-decimal tolerant number parse; null for blank or unparseable input. */
export function parseClipboardNumber(raw: string | number | null | undefined): number | null {
  const text = raw?.toString().replace(',', '.').trim();
  if (!text) return null;
  const parsed = parseFloat(text);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Excel terminates the clipboard with a newline; drop the resulting empty row. */
export function trimTrailingEmptyClipboardRow(rows: string[][]): string[][] {
  const tail = rows[rows.length - 1];
  return tail?.length === 1 && tail[0] === '' ? rows.slice(0, -1) : [...rows];
}

const WALL_DATE_TIME_PATTERN = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2}))?$/;
const TZ_AWARE_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

const ZURICH_WALL_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Zurich',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function zurichOffsetMinutes(epochMs: number): number {
  const parts = ZURICH_WALL_TIME.formatToParts(new Date(epochMs));
  const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find(candidate => candidate.type === type)?.value ?? '0');
  const wallAsUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  return Math.round((wallAsUtc - epochMs) / 60_000);
}

const padTwo = (value: number): string => String(value).padStart(2, '0');

/**
 * Parses a pasted/typed timestamp into a tz-aware ISO string (the API rejects
 * naive instants). Accepts `DD.MM.YYYY[ HH:MM]` read as Europe/Zurich wall
 * time (no time = midnight) and tz-aware ISO strings, which pass through.
 */
export function parseClipboardDateTime(raw: string | number | null | undefined): string | null {
  const text = raw?.toString().trim();
  if (!text) return null;
  if (TZ_AWARE_ISO_PATTERN.test(text)) {
    return Number.isNaN(Date.parse(text)) ? null : text;
  }
  const match = WALL_DATE_TIME_PATTERN.exec(text);
  if (!match) return null;
  const [, dayRaw, monthRaw, yearRaw, hourRaw, minuteRaw] = match;
  const day = Number(dayRaw);
  const month = Number(monthRaw);
  const year = Number(yearRaw);
  const hour = Number(hourRaw ?? '0');
  const minute = Number(minuteRaw ?? '0');
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const probe = new Date(wallAsUtc);
  // Date.UTC silently rolls invalid components (31.02, 24:00) into the next unit.
  const overflowed =
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day ||
    probe.getUTCHours() !== hour ||
    probe.getUTCMinutes() !== minute;
  if (overflowed) return null;
  // Two passes converge on the DST-correct offset for that wall time.
  const offset = zurichOffsetMinutes(wallAsUtc - zurichOffsetMinutes(wallAsUtc) * 60_000);
  const sign = offset < 0 ? '-' : '+';
  const magnitude = Math.abs(offset);
  return (
    `${year}-${padTwo(month)}-${padTwo(day)}T${padTwo(hour)}:${padTwo(minute)}:00` +
    `${sign}${padTwo(Math.floor(magnitude / 60))}:${padTwo(magnitude % 60)}`
  );
}

/**
 * Maps a pasted choice label to its option id (case-insensitive, trimmed);
 * integer input matching an option id is accepted as-is. Unmatched text is
 * returned verbatim so the grid can flag the cell instead of dropping data.
 */
export function matchChoiceLabel(
  raw: string | number | null | undefined,
  options: readonly ClipboardChoiceOption[],
): string | number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return raw;
  const text = raw.trim();
  if (!text) return null;
  const lowered = text.toLowerCase();
  const byLabel = options.find(option => option.label.trim().toLowerCase() === lowered);
  if (byLabel) return byLabel.id;
  const numeric = Number(text);
  if (Number.isInteger(numeric) && options.some(option => option.id === numeric)) return numeric;
  return text;
}

/** Builds a `processCellFromClipboard` callback from a per-column type resolver. */
export function createClipboardCellParser(
  resolveColumnType: (colId: string) => ClipboardColumnType,
): (params: ProcessCellForExportParams) => string | number | null {
  return params => {
    const raw = params.value as string | number | null | undefined;
    const columnType = resolveColumnType(params.column.getColId());
    switch (columnType.kind) {
      case 'numeric':
        return parseClipboardNumber(raw);
      case 'date':
        return parseClipboardDateTime(raw);
      case 'choice':
        return matchChoiceLabel(raw, columnType.options);
      case 'text':
        return raw === null || raw === undefined ? null : String(raw);
      default:
        return raw ?? null;
    }
  };
}

/** Input types with no caret: keystrokes on them are not text entry (Escape on a toggle switch must still reach the grid). */
const NON_TEXT_INPUT_TYPES = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

/**
 * True when a keyboard event originated inside a text-entry control (cell editor
 * input, filter field, dialog form...). The keystroke edits or dismisses that
 * control — e.g. the Escape that closed the cell editor bubbles up with editing
 * already stopped — so document-level shortcuts must not re-act on it.
 */
export function isTextEntryEvent(event: Event): boolean {
  const target = event.target;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  // `=== true` guards the boolean contract: jsdom leaves isContentEditable undefined.
  return target instanceof HTMLElement && target.isContentEditable === true;
}

/**
 * Layers that own their keystrokes while open: PrimeNG dialogs/popovers/drawers
 * (they close on Escape without preventDefault nor stopPropagation) and AG Grid
 * popups — column menus, filter panels (same: close without consuming the event).
 */
const KEY_OWNING_OVERLAY_SELECTOR = '.ag-popup, .p-dialog, .p-popover, .p-drawer, .p-overlay, [role="dialog"], [role="alertdialog"]';

/**
 * True when a document-level keystroke belongs to another UI layer than the grid:
 * already consumed by a widget (`defaultPrevented`, e.g. the datepicker closing
 * its panel), typed in a text-entry control, or aimed at an open overlay. Grid
 * selection shortcuts (Escape clears, Delete blanks) must stand down, otherwise
 * they wipe the selection/focused cell the user still needs for the follow-up
 * copy/paste.
 */
export function isKeystrokeClaimedElsewhere(event: KeyboardEvent): boolean {
  if (event.defaultPrevented || isTextEntryEvent(event)) return true;
  const target = event.target;
  return target instanceof Element && target.closest(KEY_OWNING_OVERLAY_SELECTOR) !== null;
}

/**
 * `suppressKeyboardEvent` handler for editable grids: Ctrl/Cmd+V while a cell
 * editor is open commits the edit and replays the paste on the grid, and
 * ArrowUp/ArrowDown/Enter commit then move the editor to the adjacent row
 * (Excel-style fast entry). Inert outside of editing.
 */
export function gridEditingKeyboardHandler<TData>(params: SuppressKeyboardEventParams<TData>): boolean {
  if (!params.editing) return false;

  if ((params.event.ctrlKey || params.event.metaKey) && params.event.key === 'v') {
    params.api.stopEditing(false);
    setTimeout(() => params.api.pasteFromClipboard(), 0);
    return true;
  }

  if (params.event.key === 'ArrowLeft' || params.event.key === 'ArrowRight') {
    return false;
  }

  if (params.event.key === 'ArrowUp' || params.event.key === 'ArrowDown' || params.event.key === 'Enter') {
    params.event.preventDefault();
    const currentCell = params.api.getFocusedCell();
    if (!currentCell) return true;

    const direction = params.event.key === 'ArrowUp' ? -1 : 1;
    const nextRowIndex = currentCell.rowIndex + direction;
    const rowCount = params.api.getDisplayedRowCount();

    if (nextRowIndex < 0 || nextRowIndex >= rowCount) {
      params.api.stopEditing(false);
      return true;
    }

    const nextRowNode = params.api.getDisplayedRowAtIndex(nextRowIndex);
    if (!nextRowNode) return true;

    const colDef = currentCell.column.getColDef();
    const isNextEditable: boolean =
      typeof colDef.editable === 'function'
        ? Boolean(colDef.editable({ ...params, node: nextRowNode, data: nextRowNode.data as TData }))
        : Boolean(colDef.editable);

    params.api.stopEditing(false);
    params.api.clearCellSelection();
    params.api.setFocusedCell(nextRowIndex, currentCell.column);

    if (isNextEditable) {
      setTimeout(() => {
        params.api.startEditingCell({
          rowIndex: nextRowIndex,
          colKey: currentCell.column.getColId(),
        });
      }, 0);
    }
    return true;
  }

  return false;
}
